import { useEffect, useRef, useState } from "react";

interface ReplayViewerProps {
  /** URL the embedded viewer should fetch the .slp from (e.g. /api/download/<folder>/<file>). */
  replayUrl: string;
  /** Optional starting frame. */
  startFrame?: number;
  /** Height of the viewer iframe. Defaults to 720px (toolbar + game + side panel). */
  height?: number | string;
}

/**
 * Embeds the Slippi Lab replay viewer (a separately-built Solid/Vite app served
 * out of /public/lab) via an iframe. The iframe keeps Slippi Lab's Tailwind
 * styles and Solid runtime fully isolated from this React + Bootstrap app.
 *
 * The slim embed entry (lab/embed.html) reads `?replayUrl=` and auto-loads it.
 */
// In dev, `bun run dev` sets this to the Slippi Lab Vite dev server origin
// (e.g. http://localhost:5173) so the iframe loads from it with live Solid HMR.
// Unset in production → loads the prebuilt bundle from the Bun server at /lab.
// Bun inlines `process.env.BUN_PUBLIC_LAB_ORIGIN` only when the var is set (dev).
// When it's unset (dev:server / prod) the reference is left as-is, and bare
// `process` doesn't exist in the browser - so guard it with `typeof process`.
const LAB_ORIGIN = (typeof process !== "undefined" ? process.env.BUN_PUBLIC_LAB_ORIGIN : "") ?? "";

/** Read the host's current Bootstrap theme (set on <html data-bs-theme> in App.tsx). */
function getHostTheme(): "light" | "dark" {
  return document.documentElement.getAttribute("data-bs-theme") === "dark" ? "dark" : "light";
}

export function ReplayViewer({ replayUrl, startFrame, height = 720 }: ReplayViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Seed the iframe with the current theme. Captured once so the src is stable -
  // later theme flips are pushed via postMessage (below) rather than reloading
  // the iframe, which would re-fetch and restart the replay.
  const [initialTheme] = useState(getHostTheme);

  const src =
    `${LAB_ORIGIN}/lab/embed.html?replayUrl=${encodeURIComponent(replayUrl)}` +
    `&theme=${initialTheme}` +
    (startFrame ? `#${startFrame}` : "");

  // Live-sync: when the user toggles the site theme, notify the embedded viewer.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "slplab:theme", theme: getHostTheme() },
        "*",
      );
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme"],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="rounded border overflow-hidden" style={{ height }}>
      <iframe
        ref={iframeRef}
        src={src}
        title="Slippi Lab replay viewer"
        allowFullScreen
        style={{ width: "100%", height: "100%", border: "none", display: "block" }}
      />
    </div>
  );
}
